/**
 * Synchronization-pending view (APP-001 §7).
 *
 * Shown when the backend returns 409 ACCOUNT_SYNC_PENDING: the Clerk webhook
 * has not yet created/updated the local user. Provides bounded backoff (the
 * hook drives auto-retry), a manual retry control, and a support path after a
 * reasonable number of attempts.
 */
import { Loader2, ShieldAlert, LogOut } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RequestId } from './RequestId';
import { getAppLinks } from '@/config/links';
import { useSignOutAndRedirect } from '@/auth/useSignInHelpers';
import type { ApiError } from '@/api/errors';

const SUPPORT_THRESHOLD = 4;

export function SynchronizationPendingView({
  attempt,
  onRetry,
  error,
}: {
  attempt: number;
  onRetry: () => void;
  error?: ApiError | null;
}) {
  const supportUrl = getAppLinks().support;
  const signOut = useSignOutAndRedirect();
  const showSupport = attempt >= SUPPORT_THRESHOLD;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] items-center justify-center p-6"
      data-testid="sync-pending"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-info/10 text-info">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
          <CardTitle>Setting up your account…</CardTitle>
          <CardDescription>
            We are finishing your account setup. This usually completes in a few seconds.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* No fake percentage (APP-002 §32). Bounded attempt count is informational. */}
          {attempt > 0 ? (
            <p className="text-xs text-muted-foreground">Attempt {attempt + 1} of up to 8.</p>
          ) : null}
          {error?.requestId ? <RequestId requestId={error.requestId} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRetry}>
              Retry now
            </Button>
            {showSupport ? (
              <Button size="sm" variant="ghost" asChild>
                <a href={supportUrl} rel="noopener noreferrer">
                  Contact support
                </a>
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => void signOut()}>
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
          {showSupport ? (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              Setup is taking longer than expected. If this persists, contact support and include the
              request ID above.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
